import React, { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import * as yup from "yup";
import { icons } from "../../../icons";
import { HeadLine3 } from "../../typography/styled";
import Button from "../../button";
import Input from "../../input";
import { clusterSettingsService } from "../../../api";
import { useUi } from "../../../context/ui.context";
import {
  Container,
  HeadWrapper,
  BodyModal,
  Icon,
} from "../create-node-pool/styles";

type ModalProps = {
  closeModalCancel: () => void;
  closeModal: (data?: any) => void;
  dataToEdit?: any;
};

const CreateClusterImage: FC<ModalProps> = ({ closeModal, closeModalCancel, dataToEdit }) => {
  const [t] = useTranslation();
  const { viewAlert } = useUi();
  const [loading, setLoading] = useState(false);
  const [loadingComplete, setLoadingComplete] = useState(false);
  const isEdit = !!dataToEdit?.id;

  const onSubmit = (values: any) => {
    setLoading(true);
    const action = isEdit
      ? clusterSettingsService.updateImage(dataToEdit.id, {
          name: values.name,
          image_name: values.image_name,
          openstack_id: values.openstack_id,
          os_distro: values.os_distro,
        })
      : clusterSettingsService.createImage(values);

    action
      .then(() => {
        setLoadingComplete(true);
        setTimeout(() => {
          closeModal({ success: true });
          setLoading(false);
          setLoadingComplete(false);
        }, 1500);
      })
      .catch((err) => {
        setLoading(false);
        viewAlert({
          severity: "error",
          message: err?.response?.data?.detail || t("ERROR_OCCURRED"),
        });
      });
  };

  const { values, errors, touched, handleSubmit, handleChange } = useFormik({
    enableReinitialize: true,
    validationSchema: yup.object().shape({
      name: yup.string().required(t("REQUIRED_FIELD")),
      image_name: yup.string().required(t("REQUIRED_FIELD")),
      openstack_id: yup.string().required(t("REQUIRED_FIELD")),
      os_distro: yup.string().required(t("REQUIRED_FIELD")),
      kube_version: yup.string().required(t("REQUIRED_FIELD")),
    }),
    initialValues: {
      name: dataToEdit?.name || "",
      image_name: dataToEdit?.image_name || "",
      openstack_id: dataToEdit?.openstack_id || "",
      os_distro: dataToEdit?.os_distro || "",
      kube_version: dataToEdit?.kube_version || "",
    },
    onSubmit,
  });

  return (
    <Container>
      <HeadWrapper>
        <HeadLine3 style={{ marginLeft: "10px", marginTop: "10px" }}>
          {isEdit ? t("EDIT_IMAGE") : t("CREATE_IMAGE")}
        </HeadLine3>
        <Icon onClick={closeModalCancel} src={icons.CloseIcon} />
      </HeadWrapper>
      <BodyModal>
        <Input title={t("NAME")} fieldName="name" value={values.name} handleChange={handleChange} errors={errors} touched={touched} required />
        <Input title={t("IMAGE_NAME")} fieldName="image_name" value={values.image_name} handleChange={handleChange} errors={errors} touched={touched} required />
        <Input title={t("OPENSTACK_ID")} fieldName="openstack_id" value={values.openstack_id} handleChange={handleChange} errors={errors} touched={touched} required />
        <Input title={t("OS_DISTRO")} fieldName="os_distro" value={values.os_distro} handleChange={handleChange} errors={errors} touched={touched} required />
        <Input title={t("KUBE_VERSION")} fieldName="kube_version" value={values.kube_version} handleChange={handleChange} errors={errors} touched={touched} required readOnly={isEdit} />
        <div style={{ display: "flex", gap: "6px", marginTop: "6px" }}>
          <Button variant="stroke" size="display" customStyles={{ width: "177px", justifyContent: "center" }} onClick={closeModalCancel} title={t("CANCEL")} />
          <Button variant="primary" size="display" loading={loading} loadingComplete={loadingComplete} customStyles={{ width: "177px", justifyContent: "center" }} onClick={handleSubmit} title={isEdit ? t("SAVE") : t("CREATE")} />
        </div>
      </BodyModal>
    </Container>
  );
};

export default CreateClusterImage;
